import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "../components/Icon";
import ConfirmDelete from "../components/ConfirmDelete";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";
import PhotoGallery from "../components/PhotoGallery";
import CommunicationsTimeline from "../components/CommunicationsTimeline";
import { useMailStatus, MailStatusBadge, inviteCustomerToPortal, generatePortalToken } from "../useFirestore";
import { formatPhone, loadPlacesScript, fmt, calcEstimateTotal } from "../utils";
import { STAGE_COLORS } from "./Pipeline";

function PortalInviteBadge({ customerId }) {
  const status = useMailStatus("portal-invite", customerId);
  return <MailStatusBadge status={status} />;
}

function Customers({ t, customers, addCustomer, updateCustomer, deleteCustomer, addLead, leads, deleteLead, invoices, deleteInvoice, deleteEstimate, deleteJob, comms, addComm, updateComm, deleteComm, teamMembers, estimates, jobs, role, myName, settings, onPreselectEstimate, pendingOpenId, onPendingConsumed, lang, onNavigateToEstimate, onNavigateToInvoice, onNavigateToJob, pendingCommsCustomerId, onCommsConsumed }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [commsCustomer, setCommsCustomer] = useState(null); // customer whose timeline is open
  const [custDetailTab, setCustDetailTab] = useState("summary");
  const [inviteState, setInviteState] = useState({}); // { [customerId]: { sending, sent, error } }
  const [copiedId, setCopiedId] = useState(null);
  const addressRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [guard, setGuard] = useState(null);
  const [sourceIsOther, setSourceIsOther] = useState(false);
  const [newCustomerPrompt, setNewCustomerPrompt] = useState(null);
  const KNOWN_SOURCES = ["Referral","Website","Google"];
  useEffect(() => {
    setIsDirty(false); setGuard(null);
    setSourceIsOther(!!form.source && !KNOWN_SOURCES.includes(form.source));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);
  const tryClose = (closeFn, saveFn) => { if (!isDirty) { closeFn(); return; } setGuard({ close: closeFn, save: saveFn }); };

  useEffect(() => {
    if (modal !== "add" && modal !== "edit") return;
    let listener = null;
    loadPlacesScript().then(() => {
      if (!addressRef.current || !window.google?.maps?.places) return;
      const ac = new window.google.maps.places.Autocomplete(addressRef.current, {
        types: ["address"],
        componentRestrictions: { country: "ca" },
        fields: ["address_components"],
      });
      listener = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        let streetNumber="", route="", city="", province="", postal="";
        for (const comp of place.address_components) {
          const type = comp.types[0];
          if (type==="street_number") streetNumber = comp.long_name;
          else if (type==="route") route = comp.long_name;
          else if (type==="locality") city = comp.long_name;
          else if (type==="administrative_area_level_1") province = comp.long_name;
          else if (type==="postal_code") postal = comp.long_name;
        }
        setForm(p => ({
          ...p,
          address: [streetNumber, route].filter(Boolean).join(" "),
          city: city || p.city,
          province: province || p.province,
          postal: postal || p.postal,
        }));
      });
    }).catch((err) => { console.error('[Places] loadPlacesScript failed:', err); }); // graceful fail — user can still type manually
    return () => { if (listener) window.google?.maps?.event?.removeListener(listener); };
  }, [modal]);

  const handleInvite = async (c) => {
    setInviteState(p => ({ ...p, [c.id]: { sending: true } }));
    const result = await inviteCustomerToPortal(c, settings);
    if (result.success) {
      setInviteState(p => ({ ...p, [c.id]: { sent: true } }));
      setTimeout(() => setInviteState(p => ({ ...p, [c.id]: null })), 3000);
    } else {
      setInviteState(p => ({ ...p, [c.id]: { error: result.error } }));
      setTimeout(() => setInviteState(p => ({ ...p, [c.id]: null })), 4000);
    }
  };

  const handleCopyLink = async (c) => {
    try {
      const url = await generatePortalToken(c);
      await navigator.clipboard.writeText(url);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    }
  };

  const [custSort, setCustSortRaw] = useState(()=>localStorage.getItem("taf-customer-sort")||"firstName-asc");
  const setCustSort = (v) => { localStorage.setItem("taf-customer-sort",v); setCustSortRaw(v); };
  const [custStatusFilter, setCustStatusFilterRaw] = useState(()=>localStorage.getItem("taf-customer-status-filter")||"all");
  const setCustStatusFilter = (v) => { localStorage.setItem("taf-customer-status-filter",v); setCustStatusFilterRaw(v); };

  const CUST_TYPE_COLORS = { residential:"#2563eb", commercial:"#d97706", municipal:"#16a34a", subcontract:"#7c3aed", warranty:"#6b7280" };
  const custTypeColor = (tag) => CUST_TYPE_COLORS[tag] || "var(--red)";
  const filtered = customers
    .filter(c=>`${c.firstName} ${c.lastName} ${c.email} ${c.city}`.toLowerCase().includes(search.toLowerCase()))
    .filter(c=>{ if(custStatusFilter==="client") return (c.status||"").toLowerCase()==="client"; if(custStatusFilter==="prospect") return (c.status||"").toLowerCase()==="prospect"; return true; });
  const sorted = [...filtered].sort((a,b)=>{
    const byFirst = custSort==="firstName-asc"||custSort==="firstName-desc";
    const ka = byFirst ? `${a.firstName} ${a.lastName}`.toLowerCase() : `${a.lastName} ${a.firstName}`.toLowerCase();
    const kb = byFirst ? `${b.firstName} ${b.lastName}`.toLowerCase() : `${b.lastName} ${b.firstName}`.toLowerCase();
    const asc = custSort==="firstName-asc"||custSort==="lastName-asc";
    return asc ? ka.localeCompare(kb,"fr") : kb.localeCompare(ka,"fr");
  });

  const openAdd = () => { setForm({firstName:"",lastName:"",company:"",email:"",phone:"",address:"",city:"Québec",province:"Québec",postal:"",status:"Prospect",source:"",notes:""}); setModal("add"); };
  const openEdit = (c) => { setForm({...c}); setModal("edit"); };

  useEffect(() => {
    if (pendingOpenId && customers.length > 0) {
      const c = customers.find(x => x.id === pendingOpenId);
      if (c) { openEdit(c); onPendingConsumed(); }
    }
  // eslint-disable-next-line
  }, [customers, pendingOpenId]);

  useEffect(() => {
    if (pendingCommsCustomerId && customers.length > 0) {
      const c = customers.find(x => x.id === pendingCommsCustomerId);
      if (c) { setCommsCustomer(c); setCustDetailTab("communications"); onCommsConsumed(); }
    }
  // eslint-disable-next-line
  }, [customers, pendingCommsCustomerId]);
  const save = async () => {
    const trimmedForm = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    if (modal==="add") {
      const newCustomer = await addCustomer(trimmedForm);
      await addLead({
        name: `${trimmedForm.firstName} ${trimmedForm.lastName}`,
        customerId: newCustomer?.id || "",
        value: 0,
        stage: "new",
        service: trimmedForm.clientTypeTag==="commercial" ? "Commercial"
               : trimmedForm.clientTypeTag==="municipal" ? "Municipal"
               : trimmedForm.clientTypeTag==="subcontract" ? "Sous-traitance"
               : "Hardscape",
        date: new Date().toISOString().slice(0,10),
        notes: trimmedForm.notes || ""
      });
      setModal(null);
      setNewCustomerPrompt({ customerId: newCustomer.id, customerName: `${trimmedForm.firstName} ${trimmedForm.lastName}` });
    } else {
      await updateCustomer(trimmedForm.id, trimmedForm);
      setModal(null);
    }
  };

  return (
    <div>
      <div className="page-header"><div className="page-header-row"><div><h1>{t.customers.title}</h1><p>{customers.length} {t.customers.subtitle}</p></div><button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={14}/>{t.customers.addBtn}</button></div></div>
      <div className="page-body">
        <div className="toolbar">
          <div className="search-wrap"><Icon name="search"/><input className="search-input" placeholder={t.customers.search} value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select className="filter-select" value={custSort} onChange={e=>setCustSort(e.target.value)}><option value="firstName-asc">{t.customers.sortFirstAZ}</option><option value="firstName-desc">{t.customers.sortFirstZA}</option><option value="lastName-asc">{t.customers.sortLastAZ}</option><option value="lastName-desc">{t.customers.sortLastZA}</option></select>
        </div>
        <div className="cust-filter-pills">
          {[["all",t.customers.customerFilterAll],["client",t.customers.customerFilterClient],["prospect",t.customers.customerFilterProspect]].map(([v,label])=>(
            <button key={v} className={`cust-pill${custStatusFilter===v?" cust-pill-active":""}`} onClick={()=>setCustStatusFilter(v)}>{label}</button>
          ))}
        </div>
        <div className="card">
          {sorted.map(c=>(
            <div key={c.id} className="list-row cust-row" style={{borderLeftColor:custTypeColor(c.clientTypeTag)}} onClick={()=>openEdit(c)}>
              {/* Desktop: single-line layout */}
              <div className="cust-row-desktop">
                <span className="cust-row-name">{c.firstName} {c.lastName}{c.company&&<span className="cust-row-company"> · {c.company}</span>}</span>
                {c.city&&<span className="cust-row-city">{c.city}</span>}
                {c.phone&&<span className="cust-row-phone">{c.phone}</span>}
              </div>
              {/* Fixed-width badge slots — always reserve column space */}
              <div className="cust-row-badge-status">
                {c.status&&<span className="badge badge-blue" style={{fontSize:10,whiteSpace:"nowrap"}}>{c.status}</span>}
              </div>
              <div className="cust-row-badge-sector">
                {c.clientTypeTag&&<span className="badge badge-gray" style={{fontSize:10,whiteSpace:"nowrap"}}>{t.customers.clientTypeTags[c.clientTypeTag]||c.clientTypeTag}</span>}
              </div>
              {/* Mobile info block: name, address, contact */}
              <div className="cust-row-mobile-info">
                <span className="cust-row-mobile-info-name">{c.firstName} {c.lastName}</span>
                <span className="cust-row-mobile-info-addr">{[c.address,c.city].filter(Boolean).join(", ")}</span>
                <span className="cust-row-mobile-info-contact">{[c.phone,c.email].filter(Boolean).join("  ")}</span>
              </div>
              {/* Mobile badges */}
              <div className="cust-row-mobile-bottom">
                {c.status&&<span className="badge badge-blue" style={{fontSize:10,whiteSpace:"nowrap"}}>{c.status}</span>}
                {c.clientTypeTag&&<span className="badge badge-gray" style={{fontSize:10,whiteSpace:"nowrap"}}>{t.customers.clientTypeTags[c.clientTypeTag]||c.clientTypeTag}</span>}
              </div>
              {/* Row 4: actions */}
              <div className="list-row-actions" onClick={e=>e.stopPropagation()}>
                <button className="btn-icon" title="Communications" onClick={()=>{setCommsCustomer(c);setCustDetailTab("communications");}}><Icon name="phone" size={14}/></button>
                {c.email&&(()=>{
                  const st = inviteState[c.id]||{};
                  const isSent = st.sent; const isError = st.error; const isSending = st.sending;
                  return (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{fontSize:11,padding:"3px 9px",whiteSpace:"nowrap",...(isSent?{color:"#16a34a",borderColor:"#16a34a"}:isError?{color:"var(--red)",borderColor:"var(--red)"}:{})}}
                      disabled={isSending}
                      title={lang==="fr"?"Inviter sur le portail":"Invite to portal"}
                      onClick={()=>handleInvite(c)}
                    >
                      {isSending?"…":isSent?`✓ ${lang==="fr"?"Envoyé":"Sent"}`:isError?"!":lang==="fr"?"Inviter":"Invite"}
                    </button>
                  );
                })()}
                <button
                  className="btn-icon"
                  title={copiedId===c.id?(lang==="fr"?"Copié!":"Copied!"):(lang==="fr"?"Copier le lien":"Copy link")}
                  onClick={()=>handleCopyLink(c)}
                  style={{color:copiedId===c.id?"var(--green)":"var(--text-3)",fontSize:13}}
                >
                  {copiedId===c.id?<span style={{fontSize:11,fontWeight:700}}>✓</span>:<span>🔗</span>}
                </button>
                {c.email&&c.portalToken&&<PortalInviteBadge customerId={c.id}/>}
                <button className="btn-icon" title={t.common.edit} onClick={()=>openEdit(c)}><Icon name="edit" size={14}/></button>
                <button className="btn-icon danger" title={t.common.delete} onClick={e=>{e.stopPropagation();setConfirmDelete(c);}}><Icon name="trash" size={14}/></button>
              </div>
            </div>
          ))}
          {sorted.length===0&&<div className="empty-state"><div className="empty-icon"><Icon name="customers" size={36}/></div><div className="empty-title">{t.customers.title}</div></div>}
        </div>
      </div>
      {(modal==="add"||modal==="edit")&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal(null),save)}>
          <div className="modal">
            <div className="modal-header"><div><div className="modal-title">{modal==="add"?t.customers.addTitle:t.customers.editTitle}</div></div><button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal(null),save)}><Icon name="x"/></button></div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              <div className="form-grid form-grid-2">
                {[["firstName",t.customers.firstName,true],["lastName",t.customers.lastName,true]].map(([k,label,req])=>(
                  <div key={k} className="form-group"><label>{label}{req&&<span> *</span>}</label><input className="form-input" value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/></div>
                ))}
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.customers.customerCompany}</label><input className="form-input" value={form.company||""} onChange={e=>setForm(p=>({...p,company:e.target.value}))}/></div>
                <div className="form-group"><label>{t.customers.email}</label><input className="form-input" value={form.email||""} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div>
                <div className="form-group"><label>{t.customers.phone}</label><input className="form-input" value={form.phone||""} onChange={e=>setForm(p=>({...p,phone:formatPhone(e.target.value)}))}/></div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.customers.address}</label><input ref={addressRef} className="form-input" value={form.address||""} onChange={e=>setForm(p=>({...p,address:e.target.value}))} autoComplete="off"/></div>
                {[["city",t.customers.city],["province",t.customers.province],["postal",t.customers.postal]].map(([k,label])=>(
                  <div key={k} className="form-group"><label>{label}</label><input className="form-input" value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/></div>
                ))}
                <div className="form-group">
                  <label>{t.customers.status}</label>
                  <select className="form-select" value={form.status||""} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                    {["Prospect","Client"].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t.customers.clientTypeTag}</label>
                  <select className="form-select" value={form.clientTypeTag||""} onChange={e=>setForm(p=>({...p,clientTypeTag:e.target.value}))}>
                    <option value="">—</option>
                    {Object.entries(t.customers.clientTypeTags).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>{t.customers.customerSource}</label>
                  <select className="form-select" value={sourceIsOther ? "_other" : (form.source||"")} onChange={e=>{
                    if(e.target.value==="_other"){ setSourceIsOther(true); setForm(p=>({...p,source:""})); }
                    else { setSourceIsOther(false); setForm(p=>({...p,source:e.target.value})); }
                  }}>
                    <option value="">—</option>
                    <option value="Referral">Referral</option>
                    <option value="Website">Website</option>
                    <option value="Google">Google</option>
                    <option value="_other">{t.customers.customerSourceOther}…</option>
                  </select>
                  {sourceIsOther&&(
                    <input className="form-input" style={{marginTop:8}} placeholder={t.customers.customerSourceOther} value={form.source||""} onChange={e=>setForm(p=>({...p,source:e.target.value}))}/>
                  )}
                </div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.customers.notes}</label><textarea className="form-textarea" value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal(null),save)}>{t.customers.cancel}</button><button className="btn btn-primary" onClick={save}>{t.customers.save}</button><button className="btn btn-secondary cust-cancel-mobile" onClick={()=>tryClose(()=>setModal(null),save)}>{t.customers.cancel}</button></div>
          </div>
        </div>
      )}
      {guard&&<UnsavedChangesGuard t={t} onSave={async()=>{await guard.save();setGuard(null);setIsDirty(false);}} onDiscard={()=>{guard.close();setGuard(null);setIsDirty(false);}} onKeepEditing={()=>setGuard(null)}/>}
      {confirmDelete&&(()=>{
        const cid = confirmDelete.id;
        const linkedLeads = (leads||[]).filter(l=>l.customerId===cid).length;
        const linkedEstimates = (estimates||[]).filter(e=>e.customerId===cid).length;
        const linkedInvoices = (invoices||[]).filter(i=>i.customerId===cid).length;
        const linkedJobs = (jobs||[]).filter(j=>j.customerId===cid).length;
        const warningParts = [];
        if (linkedLeads>0) warningParts.push(`${linkedLeads} entrée(s) pipeline`);
        if (linkedEstimates>0) warningParts.push(`${linkedEstimates} soumission(s)`);
        if (linkedInvoices>0) warningParts.push(`${linkedInvoices} facture(s)`);
        if (linkedJobs>0) warningParts.push(`${linkedJobs} projet(s)`);
        const warningText = warningParts.length>0
          ? `Ce client a: ${warningParts.join(", ")}. Ces enregistrements seront également supprimés. Êtes-vous sûr de vouloir supprimer ce client?`
          : `Êtes-vous sûr de vouloir supprimer ce client?`;
        return <ConfirmDelete message={`Supprimer ${confirmDelete.firstName} ${confirmDelete.lastName}? ${warningText}`} onConfirm={async()=>{
          await deleteCustomer(cid);
          await Promise.all([
            ...(leads||[]).filter(l=>l.customerId===cid).map(l=>deleteLead(l.id)),
            ...(estimates||[]).filter(e=>e.customerId===cid).map(e=>deleteEstimate(e.id)),
            ...(invoices||[]).filter(i=>i.customerId===cid).map(i=>deleteInvoice(i.id)),
            ...(jobs||[]).filter(j=>j.customerId===cid).map(j=>deleteJob(j.id)),
            ...(comms||[]).filter(c=>c.customerId===cid).map(c=>deleteComm(c.id)),
          ]);
          setConfirmDelete(null);
        }} onCancel={()=>setConfirmDelete(null)}/>;
      })()}
      {commsCustomer&&(
        <div className="modal-backdrop" style={{zIndex:200}} onClick={e=>e.target===e.currentTarget&&setCommsCustomer(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}><Icon name="customers" size={16}/>{commsCustomer.firstName} {commsCustomer.lastName}</div>
                <div className="modal-sub">{comms.filter(c=>c.customerId===commsCustomer.id).length} communications</div>
              </div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setCommsCustomer(null)}><Icon name="x"/></button>
            </div>
            <div style={{display:"flex",gap:2,padding:"0 20px",borderBottom:"1px solid var(--border)"}}>
              {[
                ["summary", lang==="fr"?"Sommaire":"Summary", <Icon name="list" size={13}/>],
                ["communications","Communications",<Icon name="phone" size={13}/>],
                ["photos","Photos",<Icon name="camera" size={13}/>],
              ].map(([id,label,icon])=>(
                <button key={id} onClick={()=>setCustDetailTab(id)} style={{display:"flex",alignItems:"center",gap:5,padding:"10px 14px",fontSize:12,fontWeight:600,border:"none",background:"none",cursor:"pointer",borderBottom:`2px solid ${custDetailTab===id?"var(--red)":"transparent"}`,color:custDetailTab===id?"var(--red)":"var(--text-3)"}}>
                  {icon}{label}
                </button>
              ))}
            </div>
            <div className="modal-body">
              {custDetailTab==="summary"&&(()=>{
                const cid = commsCustomer.id;
                const custLeads    = (leads||[]).filter(l=>l.customerId===cid);
                const custEstimates = (estimates||[]).filter(e=>e.customerId===cid).sort((a,b)=>(b.createdDate||"").localeCompare(a.createdDate||""));
                const custInvoices  = (invoices||[]).filter(i=>i.customerId===cid);
                const custJobs      = (jobs||[]).filter(j=>j.customerId===cid).sort((a,b)=>(b.startDate||"").localeCompare(a.startDate||""));
                const hasAny = custLeads.length||custEstimates.length||custInvoices.length||custJobs.length;
                const EST_BG = {draft:"#f3f4f6",sent:"#dbeafe",revised:"#fef9c3",approved:"#dcfce7",rejected:"#fee2e2"};
                const EST_FG = {draft:"#6b7280",sent:"#1d4ed8",revised:"#854d0e",approved:"#166534",rejected:"#dc2626"};
                const INV_BG = {Draft:"#f3f4f6",Sent:"#dbeafe",Paid:"#dcfce7"};
                const INV_FG = {Draft:"#6b7280",Sent:"#1d4ed8",Paid:"#166534"};
                const JOB_BG = {pending:"#fef9c3",inProgress:"#dbeafe",completed:"#dcfce7",cancelled:"#f3f4f6"};
                const JOB_FG = {pending:"#854d0e",inProgress:"#1d4ed8",completed:"#166534",cancelled:"#6b7280"};
                const invTotal = inv => { const sub=(inv.sections||[]).reduce((a,s)=>a+(s.price||0),0); return sub+Math.round(sub*0.05*100)/100+Math.round(sub*0.09975*100)/100; };
                const invBalance = inv => invTotal(inv)-(inv.payments||[]).reduce((a,p)=>a+(p.amount||0),0);
                const secHdr = label => <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.08em",padding:"14px 0 5px",borderBottom:"1px solid var(--border)",marginBottom:6}}>{label}</div>;
                const rowStyle = {display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",marginBottom:2};
                const badgeStyle = (bg,fg) => ({background:bg||"#f3f4f6",color:fg||"#6b7280",borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:600,whiteSpace:"nowrap",flexShrink:0});
                if (!hasAny) return <div style={{color:"var(--text-3)",fontSize:13,padding:"24px 0",textAlign:"center"}}>{lang==="fr"?"Aucun enregistrement lié":"No linked records"}</div>;
                return (
                  <div>
                    {custLeads.length>0&&<div>
                      {secHdr("Pipeline")}
                      {custLeads.map(lead=>{
                        const sc=STAGE_COLORS[lead.stage]||"#6b7280";
                        const stageLabel=t.leads?.stages?.[lead.stage]||lead.stage;
                        const linkedEsts=(estimates||[]).filter(e=>e.customerId===cid&&["approved","sent"].includes(e.status)).sort((a,b)=>(b.status==="approved"?1:0)-(a.status==="approved"?1:0));
                        const value=linkedEsts.length>0?calcEstimateTotal(linkedEsts[0]):(lead.value||0);
                        return (
                          <div key={lead.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",marginBottom:2}}>
                            <span style={{background:sc+"22",color:sc,...badgeStyle(sc+"22",sc)}}>{stageLabel}</span>
                            {value>0&&<span style={{fontSize:12,color:"var(--text-2)"}}>{fmt(value)}{linkedEsts.length>0&&<span style={{fontSize:10,color:"var(--text-3)",marginLeft:3}}>est.</span>}</span>}
                          </div>
                        );
                      })}
                    </div>}
                    {custEstimates.length>0&&<div>
                      {secHdr(lang==="fr"?"Soumissions":"Estimates")}
                      {custEstimates.map(est=>(
                        <div key={est.id} className="list-row-hover" onClick={()=>{setCommsCustomer(null);onNavigateToEstimate(est.id);}} style={rowStyle}>
                          <span style={badgeStyle(EST_BG[est.status],EST_FG[est.status])}>{est.status}</span>
                          <span style={{fontSize:13,flex:1,color:"var(--text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{est.title||est.number}</span>
                          <span style={{fontSize:12,color:"var(--text-2)",whiteSpace:"nowrap",flexShrink:0}}>{fmt(calcEstimateTotal(est))}</span>
                        </div>
                      ))}
                    </div>}
                    {custInvoices.length>0&&<div>
                      {secHdr(lang==="fr"?"Factures":"Invoices")}
                      {custInvoices.map(inv=>{
                        const total=invTotal(inv); const balance=invBalance(inv);
                        return (
                          <div key={inv.id} className="list-row-hover" onClick={()=>{setCommsCustomer(null);onNavigateToInvoice(inv.id);}} style={rowStyle}>
                            <span style={badgeStyle(INV_BG[inv.status],INV_FG[inv.status])}>{inv.status}</span>
                            <span style={{fontSize:13,flex:1,color:"var(--text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.number||inv.title}</span>
                            <span style={{fontSize:12,color:"var(--text-2)",whiteSpace:"nowrap",flexShrink:0}}>{fmt(total)}</span>
                            {inv.status!=="Paid"&&balance>0&&<span style={{fontSize:11,color:"#d97706",whiteSpace:"nowrap",flexShrink:0}}>({fmt(balance)} {lang==="fr"?"dû":"owing"})</span>}
                          </div>
                        );
                      })}
                    </div>}
                    {custJobs.length>0&&<div>
                      {secHdr(lang==="fr"?"Travaux":"Jobs")}
                      {custJobs.map(job=>(
                        <div key={job.id} className="list-row-hover" onClick={()=>{setCommsCustomer(null);onNavigateToJob(job.id);}} style={rowStyle}>
                          <span style={badgeStyle(JOB_BG[job.status],JOB_FG[job.status])}>{t.jobs?.status?.[job.status]||job.status||"—"}</span>
                          <span style={{fontSize:13,flex:1,color:"var(--text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{job.title}</span>
                          {job.startDate&&<span style={{fontSize:11,color:"var(--text-3)",whiteSpace:"nowrap",flexShrink:0}}>{job.startDate}{job.endDate&&job.endDate!==job.startDate?` → ${job.endDate}`:""}</span>}
                        </div>
                      ))}
                    </div>}
                  </div>
                );
              })()}
              {custDetailTab==="communications"&&(
                <CommunicationsTimeline comms={comms} customers={customers} estimates={estimates||[]} jobs={jobs||[]} teamMembers={teamMembers||[]} onAdd={addComm} onEdit={updateComm} onDelete={deleteComm} contextCustomerId={commsCustomer.id}/>
              )}
              {custDetailTab==="photos"&&(
                <PhotoGallery
                  entityType="customer"
                  entityId={commsCustomer.id}
                  role={role||"employee"}
                  uploadedBy={myName||""}
                  allowedCategories={["site-visit"]}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {newCustomerPrompt&&(
        <div className="modal-backdrop" onClick={()=>setNewCustomerPrompt(null)}>
          <div className="modal" style={{maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><div className="modal-title">Client ajouté ✓</div></div>
              <button className="btn-icon" onClick={()=>setNewCustomerPrompt(null)} title={t.common.close}><Icon name="x" size={18}/></button>
            </div>
            <div className="modal-body" style={{paddingBottom:8}}>
              <p style={{margin:0,fontSize:14,color:"var(--text-2)"}}>{newCustomerPrompt.customerName} a été ajouté aux clients et au pipeline. Voulez-vous créer une soumission maintenant?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setNewCustomerPrompt(null)}>{t.common.cancel}</button>
              <button className="btn btn-primary" onClick={()=>{if(onPreselectEstimate)onPreselectEstimate(newCustomerPrompt.customerId);setNewCustomerPrompt(null);}}>Créer une soumission</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
